import { useQuery } from '@apollo/client/react';
import { Avatar, Col, Divider, Flex, Skeleton, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import events from '@guuru/events-web';
import useTrackEventOnce from '@guuru/events-web/src/hooks/useTrackEventOnce';
import { scaleRating } from '@guuru/rating-common';
import Languages from '@guuru/react-languages';
import { Rating } from '@guuru/react-rating';
import { localize } from '@guuru/translation-common';
import GET_EXPERT_PROFILE from './queries/GetExpertProfile';

const { Text, Title } = Typography;

const ExpertProfile = function ({ chatId, partnerId }) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { loading, data } = useQuery(GET_EXPERT_PROFILE, {
    variables: {
      id: chatId,
      partnerId,
    },
  });

  const name = data?.chat?.partner?.name;
  const expert = data?.chat?.expert;

  const {
    id: expertId,
    name: expertName,
    photoUrl,
    languages,
    partners: { edges = [] } = {},
  } = expert || {};

  const [partner] = edges || [];

  useTrackEventOnce(() => {
    if (expertId) {
      events.expertProfileViewed();
    }
  }, [expertId]);

  const { joinedAt, rating, bio } = partner || {};
  const partnerName = localize(name, language);

  const profileDate = useMemo(
    () =>
      new Date(joinedAt).toLocaleString(language, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    [joinedAt, language],
  );

  if (!expertId) {
    return null;
  }

  if (loading) {
    return (
      <Flex vertical justify="center">
        <Space align="center" size={16} orientation="vertical">
          <Skeleton.Input active loading={loading} block />
          <Skeleton.Avatar active loading={loading} size={120} />
          <Flex />
        </Space>
        <Skeleton
          active
          block
          title={false}
          loading={loading}
          paragraph={{ rows: 4 }}
        />
      </Flex>
    );
  }

  return (
    <Flex vertical justify="center">
      <Space align="center" size={16} orientation="vertical">
        <Rating rating={scaleRating(rating)} />
        <Avatar size={120} src={photoUrl} />
        <Text type="secondary">
          {t('chat:::communityMember', { partnerName })}
        </Text>
      </Space>
      <Space align="center" size={2} orientation="vertical">
        <Title level={3}>{expertName}</Title>
        <Languages locales={languages} maxCount={3} />
        <Text>
          {t('chat:::memberSince')} {profileDate}
        </Text>
      </Space>
      {bio && (
        <Col align="center">
          <Divider />
          <Text italic>{bio}</Text>
          <Divider />
        </Col>
      )}
    </Flex>
  );
};

ExpertProfile.propTypes = {
  chatId: PropTypes.string.isRequired,
  partnerId: PropTypes.string.isRequired,
};

export default ExpertProfile;
